import * as THREE from "three";
import { asOverheadUI } from "./overheadLayer.js";
import { CHAT_BUBBLE_MS } from "@ghostland/shared";

/**
 * Floating pixel CHAT BUBBLES above players. When a player says something, a wrapped pixel
 * speech box (dark panel, white border, little downward tail) pops up over their head and stays
 * for a few seconds. Bubbles follow the player each frame and hide when the player isn't visible
 * (different zone). Keyed by sessionId — a new line replaces that player's bubble.
 */

// FS is a multiple of 8 (Press Start 2P's native grid) so the font renders pixel-perfect — no AA,
// no blur — exactly as the font looks. LH is generous because Press Start 2P's glyphs render taller
// than the nominal size, so a tight line height makes consecutive lines touch/overlap.
const PAD = 10, LH = 30, FS = 16, MAXCH = 12, MAXLINES = 4, TAIL = 14, BORDER = 3;
const FONT = `${FS}px 'Press Start 2P', ui-monospace, monospace`;
const SCALE = 1 / 165;     // canvas px → world units
const BUBBLE_Y = 1.4;      // world height of the tail tip — right above the head/name tag

/** Make sure the Press Start 2P pixel font is loaded before bubbles are drawn (else it falls back). */
function preloadFont(): void {
  if (!document.getElementById("gl-chat-font")) {
    const s = document.createElement("style"); s.id = "gl-chat-font";
    s.textContent = "@import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');";
    document.head.appendChild(s);
  }
  void (document as Document & { fonts?: { load(f: string): Promise<unknown> } }).fonts?.load(`${FS}px 'Press Start 2P'`);
}

/** Greedy word-wrap into ≤ MAXLINES lines of ≤ MAXCH chars (last line ellipsised if it overflows). */
function wrap(text: string): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= MAXCH) { cur = next; continue; }
    if (cur) lines.push(cur);
    cur = w.length <= MAXCH ? w : w.slice(0, MAXCH - 1) + "…";
    if (lines.length >= MAXLINES) break;
  }
  if (cur && lines.length < MAXLINES) lines.push(cur);
  if (lines.length > MAXLINES) lines.length = MAXLINES;
  return lines.length ? lines : [""];
}

/** A crisp pixel speech box: dark opaque panel, white border, downward tail, Press Start 2P text —
 *  all rendered pixel-perfect (font at a multiple of its native grid) and shown with NearestFilter. */
function makeBubble(text: string): HTMLCanvasElement {
  const lines = wrap(text);
  const m = document.createElement("canvas").getContext("2d")!;
  m.font = FONT;
  const textW = Math.ceil(Math.max(...lines.map((l) => m.measureText(l).width)));
  const W = textW + PAD * 2 + BORDER * 2;
  const boxH = lines.length * LH + PAD * 2;
  const H = boxH + TAIL + BORDER * 2;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d")!;
  // panel + white border
  ctx.fillStyle = "#08080c"; ctx.fillRect(0, 0, W, boxH + BORDER * 2);
  ctx.fillStyle = "#f4f4f4";
  ctx.fillRect(0, 0, W, BORDER); ctx.fillRect(0, boxH + BORDER, W, BORDER);
  ctx.fillRect(0, 0, BORDER, boxH + BORDER * 2); ctx.fillRect(W - BORDER, 0, BORDER, boxH + BORDER * 2);
  // tail (centered, pointing down)
  const tx = Math.round(W / 2);
  ctx.fillStyle = "#f4f4f4";
  for (let i = 0; i <= TAIL; i++) ctx.fillRect(tx - (TAIL - i), boxH + BORDER + i, (TAIL - i) * 2, 1);
  ctx.fillStyle = "#08080c";
  for (let i = 0; i < TAIL - 1; i++) ctx.fillRect(tx - (TAIL - 1 - i), boxH + BORDER + i, (TAIL - 1 - i) * 2, 1);
  // text — pixel-perfect Press Start 2P, solid white, no outline/AA tricks
  ctx.font = FONT; ctx.textBaseline = "middle"; ctx.textAlign = "center"; ctx.fillStyle = "#ffffff";
  lines.forEach((l, i) => ctx.fillText(l, Math.round(W / 2), Math.round(BORDER + PAD + i * LH + LH / 2)));
  return c;
}

interface Bubble { sprite: THREE.Sprite; tex: THREE.CanvasTexture; expiry: number }
type Resolve = (sessionId: string) => { x: number; y: number; z: number; visible: boolean } | null;

export class ChatBubbles {
  private readonly map = new Map<string, Bubble>();
  constructor(private readonly scene: THREE.Scene) { preloadFont(); }

  /** Show/replace `sessionId`'s bubble with `text`. */
  say(sessionId: string, text: string, now: number): void {
    this.remove(sessionId);
    const canvas = makeBubble(text);
    const tex = new THREE.CanvasTexture(canvas);
    tex.magFilter = tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace; // crisp, no blur
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
    sprite.center.set(0.5, 0); // anchored at the tail tip
    sprite.scale.set(canvas.width * SCALE, canvas.height * SCALE, 1);
    sprite.renderOrder = 999;
    asOverheadUI(sprite); // crisp overlay layer — never softened by the world depth-of-field
    this.scene.add(sprite);
    this.map.set(sessionId, { sprite, tex, expiry: now + CHAT_BUBBLE_MS });
  }

  /** Follow players + expire old bubbles. `resolve` maps a sessionId to its current world spot. */
  tick(now: number, resolve: Resolve): void {
    for (const [sid, b] of this.map) {
      if (now >= b.expiry) { this.remove(sid); continue; }
      const p = resolve(sid);
      if (!p) { this.remove(sid); continue; }
      b.sprite.visible = p.visible;
      b.sprite.position.set(p.x, p.y + BUBBLE_Y, p.z);
    }
  }

  remove(sessionId: string): void {
    const b = this.map.get(sessionId);
    if (!b) return;
    this.scene.remove(b.sprite);
    b.tex.dispose();
    (b.sprite.material as THREE.SpriteMaterial).dispose();
    this.map.delete(sessionId);
  }

  dispose(): void {
    for (const sid of [...this.map.keys()]) this.remove(sid);
  }
}
